/**
 * @module services/cache
 */

import { publicConfig } from "../util/public-config";
import { isHydrated } from "../util/is-hydrated";

const MILLISECONDS_PER_SECOND = 1000;

export const getApiPathSegment = (path) => {
  const match = /\/api\/([^\/?]+)/.exec(path);
  return match?.[1] ? match[1] + "/" : null;
};

/**
 * Return true if data for given path is
 *  - loaded and stale
 *  - not loaded
 *
 * @param {string} path
 * @return {boolean}
 */

// eslint-disable-next-line import/prefer-default-export
const validateTimeStamp = ({ timestamp, path }) => {
  if (!isHydrated()) return false;
  const { storeCache } = publicConfig;
  const pageTTL =
    storeCache.overrides[getApiPathSegment(path)] || storeCache.pageTTL;

  const lastLoadedSecondsAgo =
    (Date.now() - timestamp) / MILLISECONDS_PER_SECOND;

  return lastLoadedSecondsAgo > pageTTL;
};

export function isPageDataStale(pages, path) {
  const timestamp = pages[path] !== undefined && pages[path].timestamp;
  if (!timestamp) return true;
  return validateTimeStamp({ timestamp, path });
}

export function isCDPPageDataStale(cachedPage) {
  if (!cachedPage) return true;
  return validateTimeStamp({
    timestamp: cachedPage.timestamp,
    path: cachedPage.cacheKey,
  });
}

/**
 * (Iteration on isPageDataStale utility to make it more re-usable).
 * Given a page result from Redux cache, checks whether the data exists and hasn't expired.
 *
 * @param {Object} cachedData - Object from 'pages' property, eg: reduxState?.pages?.[myPage]
 * @param {String} pagetype - Page type key matching what is found in storeCache overrides.
 */
export const hasFreshCachedData = ({ cachedData, pagetype } = {}) => {
  if (!cachedData) return false;
  const { storeCache } = publicConfig;
  const { timestamp } = cachedData || {};
  // TTL = Time To Live
  // Check the configs for an override TTL; otherwise, use the default
  const pageTTL = storeCache.overrides[`${pagetype}/`] || storeCache.pageTTL;
  const lastLoadedSecondsAgo =
    (Date.now() - timestamp) / MILLISECONDS_PER_SECOND;
  return lastLoadedSecondsAgo < pageTTL;
};
